import {
  faBell, faBriefcase, faCaretDown, faCheck, faCity, faCog, faHome, faTable,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';
import {
  Collapse, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Nav,
  NavItem, Navbar, NavbarToggler, Spinner,
} from 'reactstrap';
import styled from 'styled-components';

import AnonymousNavigation from 'src/components/Navigation/AnonymousNavigation';
import { getHighlightedText } from 'src/components/Navigation/CustomerPropertyNavigation/helpers/getHighlightedText';
import NavigationLogo from 'src/components/Navigation/NavigationLogo';
import UserSettingsNavigation from 'src/components/Navigation/UserSettingsNavigation';
import { usePropertyDatespan } from 'src/components/Property/PropertyDatespanProvider';
import { APIConfig } from 'src/config';
import getFullAddressString from 'src/util/address';
import { INHIBIT_PORTFOLIO_VIEWS, USER_TYPE_CUSTOMER } from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import logger from 'src/util/logger';

import getFilteredProperties from './helpers/getFilteredProperties';

const RightNav = styled(Nav).attrs({
  className: 'ms-auto',
})``;

const LeftNav = styled(Nav).attrs({
  className: 'me-auto',
})``;

const PropertyDropdown = styled(DropdownMenu)`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 0;
  z-index: 10000;
`;

const FilterDropdownItem = styled(DropdownItem)`
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: var(--bs-dropdown-bg, #fff);
  padding: 0.5rem;
  border-bottom: 1px solid var(--bs-dropdown-divider-bg, var(--bs-border-color-translucent, rgba(0, 0, 0, 0.15)));
  &:hover,
  &:focus {
    background-color: var(--bs-dropdown-bg, #fff);
  }
`;

const PropertyItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const PropertyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PropertyTitle = styled.div.attrs({
  className: 'fw-semibold',
})`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PropertyAddress = styled.small.attrs({
  className: 'text-muted',
})`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 0.125rem;
`;

const SelectedIndicator = styled.div`
  flex-shrink: 0;
  margin-left: 0.5rem;
  align-self: center;
`;

const CustomerPropertyNavigationViewerFragment = graphql`
  fragment CustomerPropertyNavigation_viewer on Viewer {
    id
    viewerUser {
      email
      givenName
      familyName
      type
    }
    proposals: rules(first:10, type: TRADE_TYPE_NOMINATED, state: TRADE_RULE_STATE_PROPOSED) {
      edges {
        node {
          uuid
        }
      }
    }
  }
`;

const CustomerPropertyNavigationPropertyFragment = graphql`
  fragment CustomerPropertyNavigation_property on Property {
    uuid
    title
    community {
      uuid
      title
    }
    address {
      line1
      line2
      city
      state
      country
      postcode
    }
  }
`;

const CustomerPropertyNavigationPropertiesFragment = graphql`
  fragment CustomerPropertyNavigation_properties on Viewer
  @refetchable(queryName: "CustomerPropertyNavigationPropertiesRefetchQuery")
  @argumentDefinitions (
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 100 }
  )
  {
    id
    properties(first: $count, after: $cursor)
    @connection(key: "CustomerPropertyNavigation_properties") {
      edges {
        node {
          uuid
          title
          address {
            line1
            line2
            city
            state
            country
            postcode
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/**
 * Formats the count of proposals for display in navigation.
 * @param {object|null|undefined} ruleConn - GraphQL connection object with edges array
 * @returns {string} Formatted count: '0' for empty/null, '1'-'9' for counts 1-9, '9+' for 10+
 */
export const proposalsCount = (ruleConn) => {
  const count = ruleConn?.edges?.length ?? 0;
  if (count > 9) { return '9+'; }
  return String(count);
};

/**
 * CustomerPropertyNavigation component that displays navigation for a specific property.
 * Consumes datespan.start from PropertyDatespanContext to access current date range.
 * @param {object} props - Component props
 * @param {object} props.viewer - Viewer data with viewer and properties fragments
 * @param {object} props.property - Current property data from GraphQL
 * @returns {React.ReactComponentElement} CustomerPropertyNavigation component
 */
function CustomerPropertyNavigation(props) {
  // Props are reassigned after useFragment calls (Relay pattern)
  let { viewer, property } = props;

  const {
    data: propertiesData,
    loadNext,
    hasNext,
    isLoadingNext,
  } = usePaginationFragment(CustomerPropertyNavigationPropertiesFragment, viewer);

  const { properties } = propertiesData || {};

  viewer = useFragment(CustomerPropertyNavigationViewerFragment, viewer);
  property = useFragment(CustomerPropertyNavigationPropertyFragment, property);

  const [navbarOpen, setNavbarOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [propertyFilterTerm, setPropertyFilterTerm] = useState('');

  const dropdownScrollTopRef = useRef(null);

  // Scroll dropdown to top when search term changes to ensure filtered results are visible
  useEffect(() => {
    if (dropdownScrollTopRef.current && typeof dropdownScrollTopRef.current.scrollIntoView === 'function') {
      dropdownScrollTopRef.current.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }, [propertyFilterTerm]);

  const intl = useIntl();

  const loadingLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.load_more_button.state_loading.label',
    defaultMessage: 'Loading...',
  });
  const loadMoreLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const noMorePropertiesLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.load_more_button.state_no_more.label',
    defaultMessage: 'No more properties to load',
  });
  const portfoliosLinkLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.portfolios.link.label',
    defaultMessage: 'Portfolios',
  });
  const propertiesLinkLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.properties.link.label',
    defaultMessage: 'Properties',
  });
  const tradeRulesLinkLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.trade_rules.link.label',
    defaultMessage: 'Trade Rules',
  });
  const tradeHistoryLinkLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.trade_history.link.label',
    defaultMessage: 'Trade History',
  });
  const settingsLinkLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.property_settings.link.label',
    defaultMessage: 'Settings',
  });
  const filterPlaceholderLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.property_search.input.placeholder',
    defaultMessage: 'Filter properties...',
  });
  const noMatchingPropertiesLabel = intl.formatMessage({
    id: 'navigation.customer_property_navigation.property_search.no_results.message',
    defaultMessage: 'No matching properties',
  });

  // Get the datespan.start value from context
  const datespanContext = usePropertyDatespan();
  const { datespanStart } = datespanContext || {};

  const toggleNavbar = () => {
    setNavbarOpen((prevState) => !prevState);
  };

  const toggleProperty = () => {
    setPropertyDropdownOpen((prevState) => !prevState);
  };

  /**
   * Loads the next page of properties.
   * @returns {void}
   */
  const handleLoadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    loadNext(
      100,
      { onComplete: (error) => { if (error) { logger.log({ error }); } } },
    );
  };

  const renderPropertyNavigation = (propertyConnection, selected) => {
    if (!selected?.uuid) {
      return null;
    }
    const propertyList = propertyConnection?.edges?.length > 0
      ? getNodesFromEdges(propertyConnection)
      : [];

    const filteredPropertyList = getFilteredProperties(propertyList, propertyFilterTerm);

    const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);

    const tradesHistoryUrl = `/properties/${selected.uuid}/trades/history${
      datespanStart ? `?date=${datespanStart.toISODate()}` : ''
    }`;

    return (
      <>
        <Dropdown
          nav
          inNavbar
          isOpen={propertyDropdownOpen}
          toggle={toggleProperty}
        >
          {propertyList.length === 0 && (
          <NavItem>
            <Link to="/properties" className="nav-link">
              <FontAwesomeIcon icon={faHome} size="1x" className="me-2" />
              {propertiesLinkLabel}
            </Link>
          </NavItem>
          )}
          {propertyList.length > 0 && (
            <>
              <Link
                to={`/properties/${selected.uuid}`}
                className="nav-link"
                style={{ display: 'inline-block', paddingRight: 0 }}
              >
                <FontAwesomeIcon icon={faHome} size="1x" className="me-2" />
                {selected.title}
              </Link>
              <DropdownToggle
                nav
                className="dropdown-toggle-split"
                style={{ display: 'inline-block', paddingLeft: 0 }}
              >
                <FontAwesomeIcon
                  icon={faCaretDown}
                  size="1x"
                  className="ms-2"
                />
              </DropdownToggle>
              <PropertyDropdown>
                <div ref={dropdownScrollTopRef} aria-hidden="true" />
                <FilterDropdownItem toggle={false}>
                  <Input
                    type="search"
                    placeholder={filterPlaceholderLabel}
                    value={propertyFilterTerm}
                    onChange={(e) => setPropertyFilterTerm(e.target.value)}
                  />
                </FilterDropdownItem>
                {filteredPropertyList.length === 0 && propertyFilterTerm && (
                  <DropdownItem disabled>{noMatchingPropertiesLabel}</DropdownItem>
                )}
                {filteredPropertyList.map((propertyItem) => {
                  const { address } = propertyItem;
                  const addressString = getFullAddressString(address);
                  const isSelected = propertyItem.uuid === selected.uuid;
                  return (
                    <Link
                      to={`/properties/${propertyItem.uuid}`}
                      className={`dropdown-item ${isSelected ? 'active' : ''}`}
                      role="menuitem"
                      key={propertyItem.uuid}
                      title={addressString ? `${propertyItem.title} - ${addressString}` : propertyItem.title}
                    >
                      <PropertyItemContent>
                        <PropertyInfo>
                          <PropertyTitle>
                            {getHighlightedText(propertyItem.title, propertyFilterTerm)}
                          </PropertyTitle>
                          {addressString && (
                            <PropertyAddress>
                              {getHighlightedText(addressString, propertyFilterTerm)}
                            </PropertyAddress>
                          )}
                        </PropertyInfo>
                        {isSelected && (
                          <SelectedIndicator>
                            <span className="visually-hidden">Currently selected</span>
                            <FontAwesomeIcon icon={faCheck} size="1x" aria-hidden="true" />
                          </SelectedIndicator>
                        )}
                      </PropertyItemContent>
                    </Link>
                  );
                })}
                <DropdownItem divider />
                {hasNext ? (

                  <DropdownItem onClick={handleLoadMore} disabled={isLoadingNext} toggle={false}>
                    {isLoadingNext ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {loadingLabel}
                      </>
                    ) : (
                      loadMoreLabel
                    )}
                  </DropdownItem>
                )
                  : <DropdownItem disabled>{ noMorePropertiesLabel }</DropdownItem>}
              </PropertyDropdown>
            </>
          )}
        </Dropdown>
        {!inhibitPortfolioViews && (
          <NavItem>
            <Link to="/portfolios" className="nav-link">
              <FontAwesomeIcon icon={faCity} size="1x" className="me-2" />
              {portfoliosLinkLabel}
            </Link>
          </NavItem>
        )}
        <NavItem tabIndex={0} className="nav-trade-rules">
          <Link to={`/properties/${selected.uuid}/trade-rules/active`} className="nav-link ">
            <FontAwesomeIcon icon={faBriefcase} size="1x" className="me-2" />
            {tradeRulesLinkLabel}
          </Link>
        </NavItem>
        <NavItem>
          <Link to={tradesHistoryUrl} className="nav-link">
            <FontAwesomeIcon icon={faTable} size="1x" className="me-2" />
            {tradeHistoryLinkLabel}
          </Link>
        </NavItem>
        <NavItem>
          <Link to={`/properties/${selected.uuid}/settings`} className="nav-link">
            <FontAwesomeIcon icon={faCog} size="1x" className="me-2" />
            {settingsLinkLabel}
          </Link>
        </NavItem>
      </>
    );
  };

  const { community } = property || {};

  if (!viewer) {
    return <AnonymousNavigation />;
  }

  const { proposals, viewerUser } = viewer;
  const { type } = viewerUser || {};

  const cobrand = APIConfig().cobrand(community?.uuid, community?.title);

  const primaryBrand = APIConfig().primaryBrand();

  return (
    <Navbar light expand="lg">
      <Container className="d-flex flex-wrap justify-content-between px-0 px-sm-3">
        <Link to="/" className="navbar-brand">
          <NavigationLogo cobrand={cobrand} primaryBrand={primaryBrand} />
        </Link>
        <NavbarToggler onClick={toggleNavbar} />
        <Collapse isOpen={navbarOpen} navbar>
          <LeftNav navbar>{renderPropertyNavigation(properties, property)}</LeftNav>
          <RightNav navbar>
            {type === USER_TYPE_CUSTOMER && (
              <NavItem>
                <Link to="/trade-rules" className="nav-link">
                  <span className="fa-layers fa-fw" style={{ fontSize: '1.5rem', marginTop: '0.6rem' }}>
                    <FontAwesomeIcon icon={faBell} />
                    <span className="fa-layers-counter danger fw-bold" style={{ fontSize: '2.5rem', paddingTop: '.8rem' }}>{proposalsCount(proposals)}</span>
                  </span>
                </Link>
              </NavItem>
            )}
            <UserSettingsNavigation user={viewerUser} />
          </RightNav>
        </Collapse>
      </Container>
    </Navbar>
  );
}

CustomerPropertyNavigation.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  property: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    community: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

CustomerPropertyNavigation.defaultProps = {
  viewer: null,
};

export default CustomerPropertyNavigation;
